import { Component, Input, OnInit, ViewChildren, QueryList } from '@angular/core';
import { CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TrainingService } from '../../../../../../../../@shared/services/training.service';
import {
  EventResponse,
  TrainingPackageResponse,
  TrainingSessionResponse,
  TrainingRegistrationResponse,
  TrainingSessionAssignmentResponse
} from '../../../../../../../../@shared/models/response.module';
import { DeleteModal } from '../../../../../../../../@shared/components/delete-modal/delete-modal';

interface EventDay {
  date: Date;
  dateStr: string;
  label: string;
  shortLabel: string;
  isToday: boolean;
}

interface DayProgress {
  expected: number;  // Sessions expected for this day
  assigned: number;  // Sessions already assigned for this day
}

interface ParticipantView {
  registration: TrainingRegistrationResponse;
  userName: string;
  horseName: string;
  packageName: string;
  sessionType: 'group' | 'individual' | 'both';
  totalSessions: number;
  assignedSessions: number;
  packageDays: string[]; // ISO date strings the package is valid for
  dayProgress: Map<string, DayProgress>; // Per-day progress tracking
  isComplete: boolean;
  assignments: TrainingSessionAssignmentResponse[];
}

interface SessionView {
  session: TrainingSessionResponse;
  assignments: TrainingSessionAssignmentResponse[];
  participantCount: number;
  isFull: boolean;
  syncing?: boolean;
}

@Component({
  selector: 'app-manage-training-schedule',
  standalone: false,
  templateUrl: './manage-training-schedule.html',
  styleUrl: './manage-training-schedule.css'
})
export class ManageTrainingSchedule implements OnInit {
  @Input() event: EventResponse;
  @ViewChildren('sessionDropList') sessionDropListsQuery: QueryList<CdkDropList>;

  get sessionDropLists(): CdkDropList[] {
    return this.sessionDropListsQuery ? this.sessionDropListsQuery.toArray() : [];
  }

  // Data
  packages: TrainingPackageResponse[] = [];
  sessions: TrainingSessionResponse[] = [];
  registrations: TrainingRegistrationResponse[] = [];
  assignments: TrainingSessionAssignmentResponse[] = [];

  // Views
  participants: ParticipantView[] = [];
  daySessions: Map<string, SessionView[]> = new Map();

  // UI State
  loading = true;
  eventDays: EventDay[] = [];
  selectedDay: EventDay | null = null;

  // Filters
  showCompletedParticipants = true;
  participantFilter: 'all' | 'pending' | 'complete' = 'all';

  // Session form
  showSessionForm = false;
  editingSession: TrainingSessionResponse | null = null;
  sessionForm = {
    session_name: '',
    session_description: '',
    session_type: 'individual' as 'group' | 'individual' | 'break' | 'startlist',
    start_time: '',
    end_time: '',
    max_participants: 1,
    package_id: null as number | null,
    time_per_participant: 5,
    instructor: '',
    location: ''
  };

  constructor(
    private trainingService: TrainingService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.generateEventDays();
    this.loadData();
  }

  generateEventDays() {
    if (!this.event.start_date || !this.event.end_date) return;

    const startDate = new Date(this.event.start_date);
    const endDate = new Date(this.event.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.eventDays = [];
    const currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);

    while (currentDate <= endDate) {
      const dayDate = new Date(currentDate);
      const isToday = dayDate.getTime() === today.getTime();

      this.eventDays.push({
        date: dayDate,
        dateStr: this.formatDateToISO(dayDate),
        label: this.formatDayLabel(dayDate),
        shortLabel: this.formatShortDayLabel(dayDate),
        isToday
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    if (this.eventDays.length > 0) {
      const todayTab = this.eventDays.find(d => d.isToday);
      this.selectedDay = todayTab || this.eventDays[0];
    }
  }

  formatDayLabel(date: Date): string {
    const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' });
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${weekday} - ${day}.${month}`;
  }

  formatShortDayLabel(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}.${month}`;
  }

  /**
   * Format a Date to ISO date string (YYYY-MM-DD) using LOCAL time (not UTC)
   * This avoids timezone issues where toISOString() can shift dates
   */
  formatDateToISO(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  selectDay(day: EventDay) {
    this.selectedDay = day;
  }

  async loadData() {
    this.loading = true;

    try {
      // Load all data in parallel
      const [packages, sessions, registrations] = await Promise.all([
        this.trainingService.getPackages({ event_id: this.event.event_id }).toPromise(),
        this.trainingService.getSessions({ event_id: this.event.event_id }).toPromise(),
        this.trainingService.getRegistrations({ event_id: this.event.event_id }).toPromise()
      ]);

      this.packages = packages || [];
      this.sessions = sessions || [];
      this.registrations = registrations || [];

      // Load assignments for all sessions
      await this.loadAllAssignments();

      // Build views
      this.buildParticipantViews();
      this.buildSessionViews();

    } catch (error) {
      console.error('Error loading training schedule data:', error);
    } finally {
      this.loading = false;
    }
  }

  async loadAllAssignments() {
    this.assignments = [];

    for (const session of this.sessions) {
      try {
        const sessionAssignments = await this.trainingService
          .getAssignments({ session_id: session.session_id })
          .toPromise();
        if (sessionAssignments) {
          this.assignments.push(...sessionAssignments);
        }
      } catch (error) {
        console.error(`Error loading assignments for session ${session.session_id}:`, error);
      }
    }
  }

  buildParticipantViews() {
    this.participants = this.registrations.map(reg => {
      const pkg = this.packages.find(p => p.package_id === reg.package_id);
      const regAssignments = this.assignments.filter(a => a.registration_id === reg.registration_id);

      // Build day progress map from package days
      const dayProgress = new Map<string, DayProgress>();
      const packageDays: string[] = [];

      if (pkg?.package_days && pkg.package_days.length > 0) {
        for (const pkgDay of pkg.package_days) {
          // Parse the training_date - handle various formats
          const dateStr = this.parseTrainingDate(pkgDay.training_date);

          if (dateStr) {
            packageDays.push(dateStr);
            dayProgress.set(dateStr, {
              expected: pkgDay.session_count || 0,
              assigned: 0
            });
          }
        }
      }

      // Count assigned sessions per day
      for (const assignment of regAssignments) {
        const session = this.sessions.find(s => s.session_id === assignment.session_id);
        if (session) {
          const sessionDate = this.formatDateToISO(new Date(session.start_time));
          const progress = dayProgress.get(sessionDate);
          if (progress) {
            progress.assigned++;
          }
        }
      }

      const totalSessions = pkg?.session_count || 0;
      const assignedSessions = regAssignments.length;

      return {
        registration: reg,
        userName: reg.user ? `${reg.user.first_name} ${reg.user.last_name}` : 'Unknown User',
        horseName: reg.horse?.name || '',
        packageName: pkg?.package_name || 'Unknown Package',
        sessionType: pkg?.session_type || 'both',
        totalSessions,
        assignedSessions,
        packageDays,
        dayProgress,
        isComplete: assignedSessions >= totalSessions,
        assignments: regAssignments
      };
    });

    // Sort: incomplete first, then by name
    this.participants.sort((a, b) => {
      if (a.isComplete !== b.isComplete) {
        return a.isComplete ? 1 : -1;
      }
      return a.userName.localeCompare(b.userName);
    });
  }

  buildSessionViews() {
    this.daySessions.clear();

    for (const day of this.eventDays) {
      const daySessions: SessionView[] = this.sessions
        .filter(s => {
          const sessionDate = new Date(s.start_time);
          return this.formatDateToISO(sessionDate) === day.dateStr;
        })
        .map(session => {
          const sessionAssignments = this.assignments.filter(a => a.session_id === session.session_id);
          const maxParticipants = session.max_participants || 999;

          return {
            session,
            assignments: sessionAssignments,
            participantCount: sessionAssignments.length,
            isFull: sessionAssignments.length >= maxParticipants
          };
        })
        .sort((a, b) => {
          const timeA = new Date(a.session.start_time).getTime();
          const timeB = new Date(b.session.start_time).getTime();
          return timeA - timeB;
        });

      this.daySessions.set(day.dateStr, daySessions);
    }
  }

  get filteredParticipants(): ParticipantView[] {
    return this.participants.filter(p => {
      if (this.participantFilter === 'pending') return !p.isComplete;
      if (this.participantFilter === 'complete') return p.isComplete;
      return true;
    });
  }

  get currentDaySessions(): SessionView[] {
    if (!this.selectedDay) return [];
    return this.daySessions.get(this.selectedDay.dateStr) || [];
  }

  getSessionCountForDay(day: EventDay): number {
    return this.daySessions.get(day.dateStr)?.length || 0;
  }

  // ============================================================================
  // DRAG AND DROP
  // ============================================================================

  canDropParticipant(participant: ParticipantView, session: SessionView): boolean {
    // Check if already assigned to this session
    if (participant.assignments.some(a => a.session_id === session.session.session_id)) {
      return false;
    }

    // Check if session is full
    if (session.isFull) {
      return false;
    }

    // Check if session is on a valid day for this package
    const sessionDate = this.formatDateToISO(new Date(session.session.start_time));
    if (participant.packageDays.length > 0 && !participant.packageDays.includes(sessionDate)) {
      return false;
    }

    // Check if participant is complete for this day
    const dayProgress = participant.dayProgress.get(sessionDate);
    if (dayProgress && dayProgress.assigned >= dayProgress.expected) {
      return false;
    }

    // If no day progress info, fall back to total completion
    if (!dayProgress && participant.isComplete) {
      return false;
    }

    // Check if session type matches package type
    if (session.session.session_type === 'break') {
      return false;
    }

    if (participant.sessionType !== 'both') {
      if (session.session.session_type !== participant.sessionType) {
        return false;
      }
    }

    return true;
  }

  getDropValidation(participant: ParticipantView, session: SessionView): { valid: boolean; reason: string } {
    if (participant.assignments.some(a => a.session_id === session.session.session_id)) {
      return { valid: false, reason: 'Already assigned to this session' };
    }

    if (session.isFull) {
      return { valid: false, reason: 'Session is full' };
    }

    const sessionDate = this.formatDateToISO(new Date(session.session.start_time));
    if (participant.packageDays.length > 0 && !participant.packageDays.includes(sessionDate)) {
      return { valid: false, reason: 'Package not valid for this day' };
    }

    // Check per-day completion
    const dayProgress = participant.dayProgress.get(sessionDate);
    if (dayProgress && dayProgress.assigned >= dayProgress.expected) {
      return { valid: false, reason: 'All sessions for this day already assigned' };
    }

    // Fallback to total completion if no day progress
    if (!dayProgress && participant.isComplete) {
      return { valid: false, reason: 'All sessions already assigned' };
    }

    if (session.session.session_type === 'break') {
      return { valid: false, reason: 'Cannot assign to break' };
    }

    if (participant.sessionType !== 'both' && session.session.session_type !== participant.sessionType) {
      return { valid: false, reason: `Package is for ${participant.sessionType} sessions only` };
    }

    return { valid: true, reason: '' };
  }

  onDrop(event: CdkDragDrop<SessionView>, session: SessionView) {
    const participant = event.item.data as ParticipantView;

    if (!this.canDropParticipant(participant, session)) {
      return;
    }

    this.assignParticipant(participant, session);
  }

  assignParticipant(participant: ParticipantView, session: SessionView) {
    this.trainingService.createAssignment({
      session_id: session.session.session_id,
      registration_id: participant.registration.registration_id
    }).subscribe({
      next: (assignment) => {
        // Update local state
        this.assignments.push(assignment);

        // Update participant view
        participant.assignments.push(assignment);
        participant.assignedSessions++;
        participant.isComplete = participant.assignedSessions >= participant.totalSessions;

        // Update per-day progress
        const sessionDate = this.formatDateToISO(new Date(session.session.start_time));
        const dayProgress = participant.dayProgress.get(sessionDate);
        if (dayProgress) {
          dayProgress.assigned++;
        }

        // Update session view
        session.assignments.push(assignment);
        session.participantCount++;
        session.isFull = session.participantCount >= (session.session.max_participants || 999);

        // Re-sort participants
        this.participants.sort((a, b) => {
          if (a.isComplete !== b.isComplete) {
            return a.isComplete ? 1 : -1;
          }
          return a.userName.localeCompare(b.userName);
        });
      },
      error: (err) => {
        console.error('Error creating assignment:', err);
      }
    });
  }

  removeAssignment(assignment: TrainingSessionAssignmentResponse, session: SessionView) {
    this.trainingService.deleteAssignment(assignment.assignment_id).subscribe({
      next: () => {
        // Update local state
        this.assignments = this.assignments.filter(a => a.assignment_id !== assignment.assignment_id);

        // Update session view
        session.assignments = session.assignments.filter(a => a.assignment_id !== assignment.assignment_id);
        session.participantCount--;
        session.isFull = session.participantCount >= (session.session.max_participants || 999);

        // Update participant view
        const participant = this.participants.find(p => p.registration.registration_id === assignment.registration_id);
        if (participant) {
          participant.assignments = participant.assignments.filter(a => a.assignment_id !== assignment.assignment_id);
          participant.assignedSessions--;
          participant.isComplete = participant.assignedSessions >= participant.totalSessions;

          // Update per-day progress
          const sessionDate = this.formatDateToISO(new Date(session.session.start_time));
          const dayProgress = participant.dayProgress.get(sessionDate);
          if (dayProgress && dayProgress.assigned > 0) {
            dayProgress.assigned--;
          }
        }
      },
      error: (err) => {
        console.error('Error removing assignment:', err);
      }
    });
  }

  // ============================================================================
  // SESSION MANAGEMENT
  // ============================================================================

  openSessionForm(session?: TrainingSessionResponse) {
    this.editingSession = session || null;
    console.log('[TRAINING] Opening session form, available packages:', this.packages);

    if (session) {
      const startTime = new Date(session.start_time);
      const endTime = new Date(session.end_time);

      this.sessionForm = {
        session_name: session.session_name,
        session_description: session.session_description || '',
        session_type: session.session_type,
        start_time: this.formatTimeForInput(startTime),
        end_time: this.formatTimeForInput(endTime),
        max_participants: session.max_participants || 1,
        package_id: session.package_id || null,
        time_per_participant: session.time_per_participant || 5,
        instructor: session.instructor || '',
        location: session.location || ''
      };
    } else {
      // Default values for new session
      this.sessionForm = {
        session_name: '',
        session_description: '',
        session_type: 'individual',
        start_time: '09:00',
        end_time: '09:30',
        max_participants: 1,
        package_id: null,
        time_per_participant: 5,
        instructor: '',
        location: ''
      };
    }

    this.showSessionForm = true;
  }

  closeSessionForm() {
    this.showSessionForm = false;
    this.editingSession = null;
  }

  formatTimeForInput(date: Date): string {
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  }

  saveSession() {
    if (!this.selectedDay) return;

    console.log('[TRAINING] saveSession called, sessionForm:', this.sessionForm);

    const startDateTime = new Date(this.selectedDay.date);
    const [startHours, startMinutes] = this.sessionForm.start_time.split(':').map(Number);
    startDateTime.setHours(startHours, startMinutes, 0, 0);

    const endDateTime = new Date(this.selectedDay.date);
    const [endHours, endMinutes] = this.sessionForm.end_time.split(':').map(Number);
    endDateTime.setHours(endHours, endMinutes, 0, 0);

    if (this.editingSession) {
      // Update existing session
      this.trainingService.updateSession({
        session_id: this.editingSession.session_id,
        session_name: this.sessionForm.session_name,
        session_description: this.sessionForm.session_description || undefined,
        session_type: this.sessionForm.session_type,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        max_participants: this.sessionForm.max_participants,
        package_id: this.sessionForm.session_type === 'startlist' ? this.sessionForm.package_id : undefined,
        time_per_participant: this.sessionForm.session_type === 'startlist' ? this.sessionForm.time_per_participant : undefined,
        instructor: this.sessionForm.instructor || undefined,
        location: this.sessionForm.location || undefined
      }).subscribe({
        next: (updated) => {
          // Update local state
          const idx = this.sessions.findIndex(s => s.session_id === updated.session_id);
          if (idx >= 0) {
            this.sessions[idx] = updated;
          }
          this.buildSessionViews();
          this.closeSessionForm();
        },
        error: (err) => {
          console.error('Error updating session:', err);
        }
      });
    } else {
      // Create new session
      const payload = {
        event_id: this.event.event_id,
        session_name: this.sessionForm.session_name,
        session_description: this.sessionForm.session_description || undefined,
        session_type: this.sessionForm.session_type,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        max_participants: this.sessionForm.max_participants,
        package_id: this.sessionForm.session_type === 'startlist' ? this.sessionForm.package_id : undefined,
        time_per_participant: this.sessionForm.session_type === 'startlist' ? this.sessionForm.time_per_participant : undefined,
        instructor: this.sessionForm.instructor || undefined,
        location: this.sessionForm.location || undefined
      };
      console.log('[TRAINING] Creating session with payload:', payload);
      this.trainingService.createSession(payload).subscribe({
        next: (created) => {
          this.sessions.push(created);
          // For startlist sessions, reload assignments since they were auto-created
          if (this.sessionForm.session_type === 'startlist') {
            this.loadAllAssignments().then(() => {
              this.buildParticipantViews();
              this.buildSessionViews();
            });
          } else {
            this.buildSessionViews();
          }
          this.closeSessionForm();
        },
        error: (err) => {
          console.error('Error creating session:', err);
        }
      });
    }
  }

  /**
   * Sync a startlist session with all registrations for its package.
   * Adds any missing registrations to the startlist.
   */
  async syncStartlist(sessionView: SessionView) {
    const session = sessionView.session;
    if (!session.package_id) return;

    sessionView.syncing = true;

    try {
      // Fetch fresh registrations from API to get any new ones
      const freshRegistrations = await this.trainingService
        .getRegistrations({ event_id: this.event.event_id })
        .toPromise();

      if (freshRegistrations) {
        this.registrations = freshRegistrations;
      }

      // Find all registrations for this package
      const packageRegistrations = this.registrations.filter(
        r => r.package_id === session.package_id
      );

      // Find registrations not already assigned to this session
      const assignedRegistrationIds = sessionView.assignments.map(a => a.registration_id);
      const missingRegistrations = packageRegistrations.filter(
        r => !assignedRegistrationIds.includes(r.registration_id)
      );

      if (missingRegistrations.length === 0) {
        // No new registrations, but still rebuild views in case registrations were updated
        this.buildParticipantViews();
        sessionView.syncing = false;
        return;
      }

      // Bulk assign missing registrations
      const result = await this.trainingService.bulkAssign({
        session_id: session.session_id,
        registration_ids: missingRegistrations.map(r => r.registration_id)
      }).toPromise();

      if (result?.created) {
        // Add new assignments to local state
        this.assignments.push(...result.created);

        // Update session view
        sessionView.assignments.push(...result.created);
        sessionView.participantCount = sessionView.assignments.length;
        sessionView.isFull = sessionView.participantCount >= (session.max_participants || 999);
      }

      // Rebuild participant views to update assignment counts
      this.buildParticipantViews();
    } catch (error) {
      console.error('Error syncing startlist:', error);
    } finally {
      sessionView.syncing = false;
    }
  }

  deleteSession(session: TrainingSessionResponse) {
    const modalRef = this.modalService.open(DeleteModal, { centered: true });
    modalRef.componentInstance.title = 'Delete Session';
    modalRef.componentInstance.description = `Are you sure you want to delete "${session.session_name}"? All assignments will be removed.`;

    modalRef.result.then((confirmed) => {
      if (confirmed) {
        this.trainingService.deleteSession(session.session_id).subscribe({
          next: () => {
            // Remove session and its assignments
            this.sessions = this.sessions.filter(s => s.session_id !== session.session_id);

            const sessionAssignments = this.assignments.filter(a => a.session_id === session.session_id);
            this.assignments = this.assignments.filter(a => a.session_id !== session.session_id);

            // Update participant views
            for (const assignment of sessionAssignments) {
              const participant = this.participants.find(p => p.registration.registration_id === assignment.registration_id);
              if (participant) {
                participant.assignments = participant.assignments.filter(a => a.session_id !== session.session_id);
                participant.assignedSessions--;
                participant.isComplete = participant.assignedSessions >= participant.totalSessions;
              }
            }

            this.buildSessionViews();
          },
          error: (err) => {
            console.error('Error deleting session:', err);
          }
        });
      }
    }).catch(() => {});
  }

  // ============================================================================
  // HELPERS
  // ============================================================================

  getParticipantStatusClass(participant: ParticipantView): string {
    const progress = this.getDayProgress(participant);
    if (progress) {
      // Use per-day progress
      if (progress.assigned >= progress.expected) return 'status-complete';
      if (progress.assigned > 0) return 'status-partial';
      return 'status-pending';
    }
    // Fallback to total
    if (participant.isComplete) return 'status-complete';
    if (participant.assignedSessions > 0) return 'status-partial';
    return 'status-pending';
  }

  getParticipantStatusIcon(participant: ParticipantView): string {
    const progress = this.getDayProgress(participant);
    if (progress) {
      // Use per-day progress
      if (progress.assigned >= progress.expected) return 'fa-check-circle';
      if (progress.assigned > 0) return 'fa-clock';
      return 'fa-exclamation-circle';
    }
    // Fallback to total
    if (participant.isComplete) return 'fa-check-circle';
    if (participant.assignedSessions > 0) return 'fa-clock';
    return 'fa-exclamation-circle';
  }

  getSessionTypeIcon(type: string): string {
    switch (type) {
      case 'group': return 'fa-users';
      case 'individual': return 'fa-user';
      case 'break': return 'fa-coffee';
      case 'startlist': return 'fa-list-ol';
      default: return 'fa-calendar';
    }
  }

  getSessionTypeLabel(type: string): string {
    switch (type) {
      case 'group': return 'Group';
      case 'individual': return 'Individual';
      case 'break': return 'Break';
      case 'startlist': return 'Start List';
      default: return type;
    }
  }

  // Get day progress for current selected day
  getDayProgress(participant: ParticipantView): DayProgress | null {
    if (!this.selectedDay) return null;
    return participant.dayProgress.get(this.selectedDay.dateStr) || null;
  }

  // Check if participant is complete for the selected day
  isParticipantCompleteForDay(participant: ParticipantView): boolean {
    const progress = this.getDayProgress(participant);
    if (!progress) return participant.isComplete; // Fallback to total
    return progress.assigned >= progress.expected;
  }

  formatTime(dateStr: string): string {
    const date = new Date(dateStr);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  }

  formatDuration(startStr: string, endStr: string): string {
    const start = new Date(startStr);
    const end = new Date(endStr);
    const minutes = Math.round((end.getTime() - start.getTime()) / 60000);

    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${minutes}m`;
  }

  getAssignmentUserName(assignment: TrainingSessionAssignmentResponse): string {
    const participant = this.participants.find(p => p.registration.registration_id === assignment.registration_id);
    return participant?.userName || 'Unknown';
  }

  getAssignmentHorseName(assignment: TrainingSessionAssignmentResponse): string {
    const participant = this.participants.find(p => p.registration.registration_id === assignment.registration_id);
    return participant?.horseName || '';
  }

  getParticipantStartTime(session: TrainingSessionResponse, index: number): string {
    const startTime = new Date(session.start_time);
    const interval = session.time_per_participant || 5; // Default 5 minutes
    const participantTime = new Date(startTime.getTime() + (index * interval * 60 * 1000));
    return this.formatTime(participantTime.toISOString());
  }

  isParticipantOnDay(participant: ParticipantView, day: EventDay): boolean {
    if (participant.packageDays.length === 0) return true;
    return participant.packageDays.includes(day.dateStr);
  }

  /**
   * Parse training_date from various formats to ISO date string (YYYY-MM-DD)
   * Handles formats like:
   * - "2026-01-17" (ISO)
   * - "01/17/2026, 01:00 AM" (US locale with time)
   * - "17/01/2026" (EU locale)
   */
  parseTrainingDate(dateInput: string | Date): string | null {
    if (!dateInput) return null;

    try {
      // If it's already a Date object
      if (dateInput instanceof Date) {
        return this.formatDateToISO(dateInput);
      }

      const dateStr = String(dateInput);

      // Check if it's already in ISO format (YYYY-MM-DD)
      if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
        return dateStr.substring(0, 10);
      }

      // Handle "MM/DD/YYYY, HH:MM AM/PM" format
      if (dateStr.includes('/') && dateStr.includes(',')) {
        const datePart = dateStr.split(',')[0].trim();
        const [month, day, year] = datePart.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      // Handle "MM/DD/YYYY" format
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          // Assume MM/DD/YYYY (US format)
          const [month, day, year] = parts;
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }

      // Try parsing with Date (use local time)
      const parsed = new Date(dateStr);
      if (!isNaN(parsed.getTime())) {
        return this.formatDateToISO(parsed);
      }

      return null;
    } catch {
      return null;
    }
  }

  getParticipantsForDay(day: EventDay): ParticipantView[] {
    return this.filteredParticipants.filter(p => this.isParticipantOnDay(p, day));
  }

  get pendingCount(): number {
    return this.participants.filter(p => !p.isComplete).length;
  }

  get completeCount(): number {
    return this.participants.filter(p => p.isComplete).length;
  }
}
